import { TextField, MenuItem, FormControlLabel, Switch } from '@mui/material';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { FunctionComponent } from 'react';
import { UserFormModel } from '../UserFormModel';
import { UserType } from '../../../../api/users/users.contracts';
import RoleSelect from '@/modules/users/submodules/roles/components/RoleSelect';
import { useTranslation } from '@/lib';

interface UserFormFieldsProps {
  control: Control<UserFormModel>;
  errors: FieldErrors<UserFormModel>;
  isGuestUser: boolean;
}

export const UserFormFields: FunctionComponent<UserFormFieldsProps> = ({ control, errors, isGuestUser }) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-y-2">
      <Controller
        name="name"
        control={control}
        rules={{ required: true }}
        render={({ field }) => <TextField label={t('name')} {...field} />}
      />

      <Controller
        name="email"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            error={errors.email !== undefined}
            helperText={errors.email?.message ? t(errors.email?.message) : undefined}
            label={t('email')}
            {...field}
          />
        )}
      />

      <Controller
        name="type"
        control={control}
        render={({ field }) => (
          <TextField
            select
            label={t('user_type')}
            value={field.value || UserType.OPERATOR}
            onChange={field.onChange}
            helperText={t('user_type_helper_text')}
          >
            <MenuItem value={UserType.OPERATOR}>{t('user_type_operator')}</MenuItem>
            <MenuItem value={UserType.GUEST}>{t('user_type_guest')}</MenuItem>
          </TextField>
        )}
      />

      <Controller
        name="password"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            error={errors.password !== undefined}
            helperText={errors.password?.message ? t(errors.password?.message) : t('password_helper_text')}
            label={t('password')}
            {...field}
          />
        )}
      />

      {/* Role Selection - Only show for non-guest users */}
      {!isGuestUser && (
        <Controller
          name="roleIds"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <RoleSelect
              selectedRoleId={field.value[0]}
              onChange={(selectedRole) => field.onChange([selectedRole?.dto.id])}
              helperText={errors.roleIds?.message !== undefined ? t(errors.roleIds?.message) : undefined}
            />
          )}
        />
      )}

      <Controller
        name="isActive"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Switch checked={field.value ?? true} onChange={(e) => field.onChange(e.target.checked)} />}
            label={t('active')}
          />
        )}
      />
    </div>
  );
};
